/**
 * Pieces shared by the Session Tracking list and the session page: status
 * labels, the recording area (player, processing, failures) and the dialogs.
 */

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import ReplayIcon from "@mui/icons-material/Replay";

import { formatBytes } from "../ContentBank/format";
import SecurePlayer, { formatClock } from "./SecurePlayer";
import { addPastSession, errorMessage, getUploadUrl, getViewers, registerRecording, uploadFile } from "./sessionsApi";

export const FONT = "Be Vietnam Pro";
export const btn = { textTransform: "none", fontFamily: FONT, borderRadius: "8px" };

export const STATUS = {
  upcoming: { label: "Upcoming", color: "info" },
  live: { label: "Live now", color: "error" },
  awaiting_recording: { label: "Recording on its way", color: "warning" },
  processing: { label: "Processing", color: "warning" },
  ready: { label: "Recording ready", color: "success" },
  failed: { label: "Recording failed", color: "error" },
  no_recording: { label: "No recording", color: "default" },
};

export const fmtDay = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
export const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString(undefined, {
  weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" }) : "");

export const sessionLabel = (s) =>
  [`Session ${s.number}`, fmtDay(s.starts_at), s.title].filter(Boolean).join(" · ");

export const todayIso = () => new Date().toLocaleDateString("en-CA");

export function SessionBody({ session, recording, editor, viewer, onProgress, onRetry, onRemoveRecording, controlRef, startAt, onStarted,
  theatre, onTheatre }) {
  if (session.status === "upcoming" || session.status === "live") {
    return (
      <Notice>
        {session.status === "live"
          ? "This class is live now. Join from the button above."
          : session.can_join
            ? "The class is about to start. You can join now."
            : `The class starts ${fmtWhen(session.starts_at)}. ${editor ? "" : "A Join button appears 10 minutes before."}`}
      </Notice>
    );
  }
  if (session.status === "awaiting_recording") {
    return <Notice>The recording usually appears here within an hour of the class ending.</Notice>;
  }
  if (!recording) {
    return <Notice>{editor ? "No recording yet. Upload one with the upload button." : "There is no recording for this class."}</Notice>;
  }
  if (recording.status === "queued" || recording.status === "processing") {
    return (
      <Notice>
        <CircularProgress size={14} sx={{ mr: 1, verticalAlign: "middle" }} />
        Preparing the recording for streaming. This takes a few minutes.
      </Notice>
    );
  }
  if (recording.status === "failed") {
    return (
      <Alert severity="error" action={editor && (
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button size="small" color="inherit" startIcon={<ReplayIcon />} onClick={() => onRetry(recording)}>Retry</Button>
          <Button size="small" color="inherit" onClick={() => onRemoveRecording(recording)}>Remove</Button>
        </Box>
      )}>
        {editor ? recording.error || "Processing failed." : "This recording isn't available right now."}
      </Alert>
    );
  }
  return (
    <Box sx={theatre ? { height: "100%" } : undefined}>
      <SecurePlayer key={recording.id} recording={recording} viewer={viewer} onProgress={onProgress}
        controlRef={controlRef} startAt={startAt} onStarted={onStarted} theatre={theatre} onTheatre={onTheatre} />
      <Box sx={{ display: theatre ? "none" : "flex", alignItems: "center", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
        {recording.duration_seconds > 0 && (
          <Typography sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>Length {formatClock(recording.duration_seconds)}</Typography>
        )}
        <Typography sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>· You've watched {recording.my_percent || 0}%</Typography>
        {editor && (
          <>
            {recording.size_bytes > 0 && (
              <Typography sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>· {formatBytes(recording.size_bytes)}</Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Button size="small" color="error" sx={btn} onClick={() => onRemoveRecording(recording)}>Remove recording</Button>
          </>
        )}
      </Box>
    </Box>
  );
}

export const Notice = ({ children }) => (
  <Box sx={{ p: 2, borderRadius: 2, backgroundColor: "#f3f8fb", border: "1px solid #dbe7ef" }}>
    <Typography sx={{ fontSize: 14, fontFamily: FONT, color: "#374151" }}>{children}</Typography>
  </Box>
);

export function ScheduleDialog({ open, mode, session, busy, onClose, onSubmit, oneToOne = false }) {
  const defaultMinutes = oneToOne ? 60 : 120;   // a 1:1 class is an hour; a batch class two
  const [form, setForm] = useState({ title: "", description: "", date: todayIso(), time: "19:00", duration: defaultMinutes });
  useEffect(() => {
    if (!open) return;
    if (mode === "reschedule" && session?.starts_at) {
      const start = new Date(session.starts_at);
      const minutes = session.ends_at ? Math.round((new Date(session.ends_at) - start) / 60000) : defaultMinutes;
      setForm({
        title: session.title || "",
        description: session.description || "",
        date: start.toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }),
        time: start.toLocaleTimeString("en-GB", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" }),
        duration: minutes,
      });
    } else {
      setForm({ title: "", description: "", date: todayIso(), time: "19:00", duration: defaultMinutes });
    }
  }, [open, mode, session, defaultMinutes]);
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const { title, description, ...slot } = form;
    onSubmit(mode === "reschedule"
      ? { ...slot, duration: Number(form.duration) }
      : { ...form, duration: Number(form.duration) });
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 700 }}>{mode === "reschedule" ? "Reschedule session" : "Schedule a session"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          {mode !== "reschedule" && (
            <>
              <TextField size="small" label="Name (optional)" placeholder="e.g. Solution discussion" value={form.title} onChange={set("title")} />
              <TextField size="small" label="Description (optional)" value={form.description} onChange={set("description")}
                multiline minRows={2} />
            </>
          )}
          <TextField size="small" type="date" label="Date" value={form.date} onChange={set("date")} required InputLabelProps={{ shrink: true }} />
          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField size="small" type="time" label="Start (IST)" value={form.time} onChange={set("time")} required
              InputLabelProps={{ shrink: true }} sx={{ flex: 1 }} />
            <TextField size="small" select label="Length" value={form.duration} onChange={set("duration")} sx={{ flex: 1 }}>
              {[30, 45, 60, 90, 120, 150, 180, 240].map((m) => (
                <MenuItem key={m} value={m}>{m < 60 ? `${m} min` : `${m / 60} h`}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Typography sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>
            {mode === "reschedule"
              ? "Everyone invited gets the updated time."
              : oneToOne
                ? "The student and you get a Google Meet invite. Record the class from core@edgent.in and the recording appears here on its own, named after the session."
                : "Everyone in the batch gets a Google Meet invite. Record the class from core@edgent.in and the recording appears here on its own."}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy}>{mode === "reschedule" ? "Save" : "Schedule"}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

/** A class that already happened: name, date, optional description and its video, in one go. */
export function AddRecordingDialog({ open, moduleId, kind = "template", onClose, onDone }) {
  const [form, setForm] = useState({ title: "", date: todayIso(), description: "" });
  const [file, setFile] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    if (open) { setForm({ title: "", date: todayIso(), description: "" }); setFile(null); setProgress(null); setError(""); }
  }, [open]);
  const busy = progress !== null;
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Choose the video file."); return; }
    setError("");
    setProgress(0);
    let created;
    try {
      created = await addPastSession(moduleId, form, kind);
    } catch (err) {
      setProgress(null);
      setError(errorMessage(err, "Couldn't add the session"));
      return;
    }
    try {
      const target = await getUploadUrl(created.id, file);
      await uploadFile(target.upload_url, file, target.content_type, setProgress);
      const payload = await registerRecording(created.id, target.key, file.name);
      onDone(payload, created.id, "Uploaded. It will be ready to stream in a few minutes.");
    } catch (err) {
      // The session exists; the upload can be retried from its upload button.
      onDone(created, created.id, `Session added, but the upload failed: ${errorMessage(err, "try again")}. Use the upload button to retry.`);
    }
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 700 }}>Add a recording</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField size="small" label="Name" required value={form.title} onChange={set("title")} disabled={busy}
            placeholder="e.g. Code walkthrough and HLD" />
          <TextField size="small" type="date" label="Date of the class" required value={form.date} onChange={set("date")}
            InputLabelProps={{ shrink: true }} disabled={busy} />
          <TextField size="small" label="Description (optional)" value={form.description} onChange={set("description")}
            multiline minRows={2} disabled={busy} />
          <Button component="label" variant="outlined" startIcon={<FileUploadOutlinedIcon />} disabled={busy}
            sx={{ ...btn, justifyContent: "flex-start" }}>
            {file ? `${file.name} · ${formatBytes(file.size)}` : "Choose video (MP4)"}
            <input hidden type="file" accept="video/mp4,video/quicktime,.mp4,.m4v,.mov" data-testid="add-recording-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>
          {busy && (
            <Box>
              <Typography sx={{ fontSize: 13, fontFamily: FONT, mb: 0.5 }}>Uploading · {progress}%</Typography>
              <LinearProgress variant="determinate" value={progress} />
            </Box>
          )}
          {error && <Typography sx={{ fontSize: 13, color: "#b91c1c", fontFamily: FONT }}>{error}</Typography>}
          <Typography sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>
            Sessions are numbered by date, so an older class slots into its place. Keep this open until the upload finishes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy}>Add</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function EditSessionDialog({ open, session, busy, onClose, onSubmit }) {
  const [form, setForm] = useState({ title: "", description: "", date: "" });
  useEffect(() => {
    if (open && session) {
      setForm({
        title: session.title || "",
        description: session.description || "",
        date: session.starts_at ? new Date(session.starts_at).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "",
      });
    }
  }, [open, session]);
  if (!session) return null;
  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    const body = { title: form.title, description: form.description };
    if (!session.scheduled && form.date) body.date = form.date;
    onSubmit(body);
  };
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <form onSubmit={submit}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit session {session.number}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <TextField size="small" label="Name" value={form.title} onChange={set("title")} />
          <TextField size="small" label="Description (optional)" value={form.description} onChange={set("description")}
            multiline minRows={2} />
          {!session.scheduled && (
            <TextField size="small" type="date" label="Date of the class" value={form.date} onChange={set("date")}
              InputLabelProps={{ shrink: true }} helperText="Changing the date can change the session's number." />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={busy}>Save</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export function ViewersDialog({ session, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    getViewers(session.id).then(setRows).catch((e) => setError(errorMessage(e, "Couldn't load viewers")));
  }, [session.id]);
  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Who watched {sessionLabel(session)}</DialogTitle>
      <DialogContent>
        {error && <Typography color="error">{error}</Typography>}
        {!rows && !error && <LinearProgress />}
        {rows && rows.map((r) => (
          <Box key={r.user_id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.75, borderBottom: "1px solid #f1f3f5" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 14, fontFamily: FONT }}>{r.name}</Typography>
              <Typography noWrap sx={{ fontSize: 12, color: "#6b7280", fontFamily: FONT }}>{r.email}</Typography>
            </Box>
            <Box sx={{ width: 120 }}>
              <LinearProgress variant="determinate" value={r.percent} color={r.percent >= 80 ? "success" : "primary"} />
            </Box>
            <Typography sx={{ width: 44, textAlign: "right", fontSize: 13, fontFamily: FONT }}>{r.percent}%</Typography>
          </Box>
        ))}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}
